import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ConnectedQuoteView } from "./ConnectedQuoteView"; 
import { formatCurrency } from "./utils/formatters";

const CARRIER_CHOICES = [
  { key: "progressive", label: "Progressive", className: "bg-blue-600 hover:bg-blue-700 text-white" },
  { key: "accelerant", label: "Accelerant", className: "bg-red-600 hover:bg-red-700 text-white" },
  { key: "nirvana", label: "Nirvana", className: "bg-yellow-500 hover:bg-yellow-600 text-black" },
];

function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

export function ViewQuoteSubmission() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [quoteData, setQuoteData] = useState(null);
  const [submissionNumber, setSubmissionNumber] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lastFetchedIdRef = useRef(null);
  const inFlightRef = useRef(false);
  const carriersFromQuote = (() => {
    const carrierList = Array.isArray(quoteData?.carrier) ? quoteData?.carrier : quoteData?.carrier ? [quoteData?.carrier] : [];
    const step1CarrierList = Array.isArray(quoteData?.quote?.step1?.carrier) ? quoteData?.quote?.step1?.carrier : quoteData?.quote?.step1?.carrier ? [quoteData?.quote?.step1?.carrier] : [];
    const merged = [...carrierList, ...step1CarrierList]
      .filter(Boolean)
      .map((c) => c?.toString?.().toLowerCase?.())
      .filter(Boolean);
    return Array.from(new Set(merged));
  })();

  const availableCarrierChoices = CARRIER_CHOICES.filter((choice) =>
    carriersFromQuote.includes(choice.key)
  );

  const debouncedSubmission = useDebounce(submissionNumber, 800);
  const debouncedQuoteId = useDebounce(quoteId, 800);

  const fetchQuote = useCallback(
    async (queryId) => {
      if (!queryId) return;
      if (inFlightRef.current && lastFetchedIdRef.current === queryId) return;
      if (lastFetchedIdRef.current === queryId) return;
      inFlightRef.current = true;
      lastFetchedIdRef.current = queryId;
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`/api/quote/${queryId}`);
        if (res.status === 200 && res.data) {
          setQuoteData(res.data.data);
          setSelectedCarrier(""); // reset until we derive from quote data
          navigate(`/admin/quote-submission/view/${queryId}`, {
            replace: true,
          });
        } else {
          setError("Quote not found.");
          lastFetchedIdRef.current = null;
        }
      } catch (err) {
        setError("Error fetching quote data.");
        lastFetchedIdRef.current = null;
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (routeId) fetchQuote(routeId);
  }, [routeId, fetchQuote]);

  useEffect(() => {
    if (debouncedSubmission) fetchQuote(debouncedSubmission);
  }, [debouncedSubmission, routeId, fetchQuote]);

  useEffect(() => {
    if (debouncedQuoteId) fetchQuote(debouncedQuoteId);
  }, [debouncedQuoteId, routeId, fetchQuote]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = typeof dateStr === "string" ? dateStr : dateStr?.$date;
    return date ? new Date(date).toLocaleDateString("en-US") : "N/A";
  };

  useEffect(() => {
    if (availableCarrierChoices.length === 0) {
      setSelectedCarrier("");
      return;
    }
    const currentStillValid = availableCarrierChoices.some((c) => c.key === selectedCarrier);
    if (!currentStillValid) {
      setSelectedCarrier(availableCarrierChoices[0].key);
    }
  }, [availableCarrierChoices, selectedCarrier]);

  // Always render the search inputs
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center py-8">
      {/* Input Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 w-full max-w-xl">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
          Search Quote
        </h2>
        <div className="flex flex-col gap-3">
          <input
            type="text"
            placeholder="Submission Number / Quote ID..."
            value={quoteId}
            onChange={(e) => setQuoteId(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2"
          />
          {quoteId.trim().length > 0 && (
            <p className="text-sm text-gray-800">
              Please choose which carrier you are running this quote for.
            </p>
          )}
          {availableCarrierChoices.length === 0 ? (
            <p className="text-sm text-gray-500">Carrier options will appear once a quote is loaded.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableCarrierChoices.map((carrier) => {
                const isActive = selectedCarrier === carrier.key;
                return (
                  <button
                    key={carrier.key}
                    type="button"
                    onClick={() => setSelectedCarrier(carrier.key)}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${carrier.className} ${isActive ? "ring-2 ring-offset-2 ring-gray-900" : ""}`}
                  >
                    {carrier.label}
                  </button>
                );
              })}
            </div>
          )}
          {loading && (
            <p className="text-sm text-gray-500 text-center">Loading...</p>
          )}
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
        </div>
      </div>

      {/* Show quote data below the inputs */}
      {quoteData && (
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-7xl">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Commercial Auto Quote
          </h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
            <span className="font-semibold">
              Submission #: {quoteData.submission_number || "N/A"}
            </span>
            <span>•</span>
            <span>Premium: {formatCurrency(quoteData.premium)}</span>
            <span>•</span>
            <span>
              Status:{" "}
              <span className="capitalize font-medium text-blue-600">
                {quoteData.status || "N/A"}
              </span>
            </span>
          </div>

          {quoteData.quote && (
            // 👇 Connected, schema-ordered, dependency-aware view
            <ConnectedQuoteView quoteData={quoteData} selectedCarrier={selectedCarrier} />
          )}
        </div>
      )}
    </div>
  );
}
